<template>
  <div class="min-h-screen mx-auto max-w-screen-xl" id="activities-search-and-filters">
    <div class="mt-0">
      <div v-if="showDatePicker" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div class="bg-white dark:bg-gray-800 rounded-lg p-0 max-w-sm w-full mx-4">
          <div class="flex justify-between items-center p-4">
            <h2 class="text-xl underline font-semibold">{{ $t('Choose a date') }}</h2>
            <button @click="toggleDatePicker">
              <span class="sr-only">Close</span>
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div>
            <Datepicker :modelValue="date" @update:model-value="dateSelected" :modal="false" />
          </div>
        </div>
      </div>
      <AdaptableModal :isOpen="isModalOpen" @close="closeFilters">
        <template #header>
          <h2 class="text-lg font-semibold">{{ $t('Filters') }}</h2>
        </template>
        <template #default>
          <filters
            :filtersData="filtersData"
            :priceData="priceData"
            :filterSelectedCallback="filterSelected"
            :dateSelectedCallback="dateSelected"
            :priceSelectedCallback="priceSelected"
            :autoApply="false"
            :selectedFilters="filtersSelected"
            :selectedDate="date"
            :selectedPriceInterval="priceMinMax ?? {}"
          />
        </template>
        <template #footer>
          <div class="flex justify-between">
            <button
              class="text-gray-800 dark:text-white border-2 border-solid border-gray-300 hover:bg-gray-100 focus:ring-4 focus:ring-gray-300 font-medium rounded-full text-md px-4 lg:px-5 py-2 lg:py-2.5 mr-2 dark:hover:bg-gray-700 focus:outline-none dark:focus:ring-gray-800"
              @click="closeFilters"
            >
              {{ $t('Close') }}
            </button>
            <button class="ts-button" @click="applyFilters">
              {{ $t('Apply Filters') }}
            </button>
          </div>
        </template>
      </AdaptableModal>

      <div class="flex flex-col lg:flex-row gap-6 justify-center">
        <div class="flex-shrink-0 p-4 hidden lg:block rounded-lg border dark:border-zinc-700" tabindex="0">
          <filters
            :filtersData="filtersData"
            :priceData="priceData"
            :filterSelectedCallback="filterSelected"
            :dateSelectedCallback="dateSelected"
            :priceSelectedCallback="priceSelected"
            :filtersAutoApply="filtersAutoApply"
            :selectedFilters="filtersSelected"
            :selectedPriceInterval="priceMinMax ?? {}"
          />
        </div>
        <div class="flex-column w-full justify-center md:mt-0" tabindex="1">
          <div class="flex w-full space-x-2 mb-4 justify-normal md:justify-end">
            <button
              @click="openFilters"
              class="lg:hidden text-gray-800 dark:text-white border-2 border-solid border-gray-300 hover:bg-gray-100 focus:ring-4 focus:ring-gray-300 font-medium rounded-full text-sm px-4 lg:px-5 py-2 lg:py-2.5 dark:hover:bg-gray-700 focus:outline-none dark:focus:ring-gray-800"
            >
              <div class="flex items-center justify-center space-x-2 w-full">
                <img src="/img/icons/page_info.svg" class="dark:filter dark:invert dark:invert-1" />
              </div>
            </button>

            <div class="relative flex-1">
              <button
                @click="toggleDatePicker"
                ref="mobileButtonSelectDate"
                class="lg:hidden flex-1 w-full text-gray-800 dark:text-white border-2 border-solid border-gray-300 hover:bg-gray-100 focus:ring-4 focus:ring-gray-300 font-medium rounded-full text-sm px-4 lg:px-5 py-2 lg:py-2.5 mr-2 dark:hover:bg-gray-700 focus:outline-none dark:focus:ring-gray-800 h-full"
                :class="{ 'bg-blue-100 dark:bg-blue-900': date }"
              >
                <div class="flex items-center justify-center space-x-2 w-full">
                  <img src="/img/icons/calendar_month.svg" class="dark:filter dark:invert dark:invert-1" />
                  <span v-if="!date" class="text-md">{{ $t('Choose a date') }}</span>
                  <span v-else class="font-bold text-md">{{ localizedDate(new Date(date), 'long') }}</span>
                </div>
              </button>
              <!-- <div v-if="showDatePicker" class="absolute z-50 left-1/2 transform -translate-x-1/2 mt-1" @click.stop>
                <div ref="datepickerRef" class="p-1 mx-auto max-w-fit" @click.stop>
                  <Datepicker :modelValue="date" @update:model-value="dateSelected" :modal="true" />
                </div>
              </div> -->
            </div>

            <Dropdown :options="sortByOptions" :selectedKey="sortby" :onSelect="sortActivities" />
          </div>

          <div class="h-12 mb-10 block justify-center items-center">
            <ActivitiesLoader
              :restartTrigger="restartLoader"
              :loading="loading"
              :foundActivities="resultsCount ?? 0"
            ></ActivitiesLoader>
          </div>

          <!-- No Results Found component -->
          <div
            v-if="!loading && activitiesData.length === 0 && page <= 2 && !errorFetchingResults"
            class="text-center py-10"
          >
            <img
              src="/img/icons/travel_explore.svg"
              class="w-20 text-Tourscanner_blue-800 mb-10 mx-auto dark:filter dark:invert dark:invert-1"
            />

            <h3 class="text-3xl md:text-xl font-semibold mb-10 md:mb-4 px-10 md:px-0">
              {{ $t('We tried, but there are no activities that match the applied filters') }}
            </h3>
            <button
              v-if="anyFilterSelected"
              @click="removeAllFilters"
              class="text-gray-800 dark:text-white border-2 border-solid border-gray-300 hover:bg-gray-100 focus:ring-4 focus:ring-gray-300 font-medium rounded-full text-md px-4 lg:px-5 py-2 lg:py-2.5 mr-2 dark:hover:bg-gray-700 focus:outline-none dark:focus:ring-gray-800"
            >
              {{ $t('Remove all filters') }}
            </button>
          </div>

          <!-- Error Results component -->
          <div v-else-if="!loading && errorFetchingResults" class="text-center py-10">
            <img
              src="/img/icons/travel_explore.svg"
              class="w-20 text-Tourscanner_blue-800 mb-10 mx-auto dark:filter dark:invert dark:invert-1"
            />
            <h3 class="text-xl font-semibold mb-4">
              {{ $t('Ops, something went wrong while processing your request') }}
            </h3>
            <button
              @click="retrySearch"
              class="text-white bg-blue-500 hover:bg-blue-600 focus:ring-4 focus:ring-blue-300 font-medium rounded-full text-sm px-5 py-2.5 mr-2 mb-2 focus:outline-none"
            >
              {{ $t('Retry Now') }}
            </button>
          </div>

          <!-- Skeleton -->
          <ul v-if="activitiesData.length == 0 && loading" class="list-none mt-2">
            <li v-for="skeleton in Array.from({ length: 6 })" :key="skeleton" class="pb-2">
              <div class="h-[210px] md:h-[185px] dark:bg-zinc-900 bg-gray-50 py-2 px-2 md:p-2 rounded-md">
                <div class="flex flex-col md:block md:flex-none justify-between h-full">
                  <div class="flex flex-row">
                    <!-- Image -->
                    <SkeletonLoader class="w-24 h-24 md:w-[10.5rem] md:h-[10.5rem]" />

                    <!-- Content -->
                    <div class="flex flex-col ml-4 flex-1 justify-between">
                      <div class="flex flex-col">
                        <SkeletonLoader class="w-full md:w-2/3 h-[20px] md:h-[20px]" />
                        <SkeletonLoader class="w-full md:w-2/3 h-[20px] md:h-[20px] mt-2" />
                      </div>

                      <div class="md:flex hidden flex-row">
                        <div class="flex flex-col mt-2 w-1/2">
                          <SkeletonLoader class="w-full h-[20px] md:h-[20px] mt-6" />
                          <SkeletonLoader class="w-full h-[20px] md:h-[20px] mt-2" />
                        </div>
                        <div class="flex flex-col mt-2 w-1/2 justify-end items-end">
                          <SkeletonLoader class="w-1/2 h-[20px] md:h-[20px] mt-6" />
                          <SkeletonLoader class="w-1/2 h-[20px] md:h-[20px] mt-2" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Mobile -->
                  <div class="flex md:hidden flex-row w-full">
                    <div class="flex flex-col mt-2 w-1/2">
                      <SkeletonLoader class="w-full h-[20px] md:h-[20px] mt-6" />
                      <SkeletonLoader class="w-full h-[20px] md:h-[20px] mt-2" />
                    </div>
                    <div class="flex flex-col mt-2 w-1/2 justify-end items-end">
                      <SkeletonLoader class="w-1/2 h-[20px] md:h-[20px] mt-6" />
                      <SkeletonLoader class="w-1/2 h-[20px] md:h-[20px] mt-2" />
                    </div>
                  </div>
                </div>
              </div>
            </li>
          </ul>
          <ul v-else class="list-none" id="activities-list">
            <li v-for="(activity, index) in activitiesData" :key="activity['id']" class="pb-2.5">
              <ActivityTile :item="activity" :selectedLanguages="selectedLanguages" />
            </li>
          </ul>
          <div class="flex w-full space-x-2 mb-4 justify-center">
            <button
              v-if="!loading && metaData.thereAreMore"
              @click="loadMoreActivities"
              class="text-white dark:text-white border-1 border-solid bg-gradient-to-r from-ts_blue_to to-ts_blue_from focus:ring-4 focus:ring-gray-300 font-medium rounded-full text-md px-4 lg:px-5 py-2 lg:py-2.5 mr-2 dark:hover:bg-gray-700 focus:outline-none dark:focus:ring-gray-800"
            >
              {{ $t('Load More') }}
            </button>
            <Spinner v-else-if="activitiesData.length != 0 && metaData.thereAreMore" class="mt-2"></Spinner>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ActivitiesLoader from '~/components/Activity/ActivitiesLoader.vue';
import AdaptableModal from '~/components/Utils/AdaptableModal.vue';
import Spinner from '~/components/Utils/Spinner.vue';
import { useCurrency } from '~/composables/useCurrency';
import Datepicker from './Filters/Datepicker.vue';

const { t } = useI18n();
const { trackingId, isLoading: isTrackingLoading } = useTrackingId();
const { currentCurrency } = useCurrency();
const mixpanel = useMixpanel();
const { proxy } = useScriptMetaPixel();

// Props
const props = defineProps({
  initialParams: {
    type: Object,
    required: true,
  },
  featuredImage: {
    type: String,
    required: false,
  },
  destinationName: {
    type: String,
    required: false,
  },
});

// Emits
const emit = defineEmits(['update:resultsCount', 'error']);

// Reactive state
const activitiesData = ref([]);
let nextBatchData = null;
const filtersData = ref([]);
const priceData = ref([]);
const metaData = ref({});
const filtersSelected = ref([]);
const date = props.initialParams.date_preSelected ? ref(props.initialParams.date_preSelected) : ref(null);
const excluded = ref([]);
const pc_groups = ref({});
const sortby = ref('suggested-DESC');
const abortController = ref(null);
const nextBatchAbortController = ref(null);
const restartLoader = ref(0);
const errorFetchingResults = ref(false);
const resultsCount = ref(0);
const loading = ref(true);
const loadingMore = ref(false);
const page = ref(0);
const isModalOpen = ref(false);
let priceMinMax = ref(null);
const sortByDropdownRef = ref(null);
const selectedLanguages = ref([]);
const showDatePicker = ref(false);
const datepickerRef = ref(null);
const mobileButtonSelectDate = ref(null);

const featuredReviews = [
  {
    name: 'Maria S.',
    text: 'Absolutely loved the personalized itinerary! It made our trip so much smoother and memorable.',
  },
  {
    name: 'John D.',
    text: "The best travel planning experience I've had. Every spot recommended was perfect for us!",
  },
  {
    name: 'Emily R.',
    text: 'We discovered amazing places we’d never have found on our own. Highly recommended!',
  },
  {
    name: 'Carlos M.',
    text: 'The itinerary was spot on! Saved us time and helped us experience the city like locals.',
  },
  {
    name: 'Sophia L.',
    text: 'Our itinerary was packed with unique experiences. It felt like it was made just for us!',
  },
  {
    name: 'Jake W.',
    text: 'A fantastic service! The expert recommendations made our trip unforgettable.',
  },
  {
    name: 'Nina T.',
    text: 'The travel expert understood exactly what we wanted. We’ll use this service again!',
  },
  {
    name: 'Oliver P.',
    text: 'Each suggestion was perfect. It made exploring the city hassle-free and enjoyable.',
  },
  {
    name: 'Laura B.',
    text: 'We felt like VIPs with our custom itinerary. It was like having a local friend guide us!',
  },
  {
    name: 'James C.',
    text: 'Exceeded expectations! Every place recommended was a highlight of our trip.',
  },
];

const anyFilterSelected = computed(() => {
  return filtersSelected.value.length > 0 || date.value || scopedSearch.value || priceMinMax.value;
});

const route = useRoute();

const scopedSearch = computed(() => {
  return route?.query?.scopedSearch || route?.query?.q || null;
});

const highlightedItemID = computed(() => {
  return route?.query?.h || null;
});

watch(
  () => route.query.scopedSearch,
  (newValue) => {
    // Handle search query changes
    scrollToResults(true);
    resetSearch();
  },
);

const calendarAttributes = {
  transparent: true,
  borderless: true,
  color: 'blue',
  highlight: false,
  'is-dark': { selector: 'html', darkClass: 'dark' },
  'first-day-of-week': 2,
};
// Computed properties
const { locale } = useI18n();

const sortByOptions = {
  'suggested-DESC': { label: t('Our Selection') },
  'price-ASC': { label: t('Lowest Price') },
  'price-DESC': { label: t('Highest Price') },
  'rating-DESC': { label: t('Best Rating') },
  'best_selling-DESC': { label: t('Most Reviews') },
  'best_selling-ASC': { label: t('Least Reviews') },
};

const filtersAutoApply = ref(false);

// Methods
function removeAllFilters() {
  filtersSelected.value = [];
  date.value = null;
  sortby.value = 'suggested-DESC';
  priceMinMax.value = null;

  if (scopedSearch.value) {
    navigateTo(
      {
        path: route.path,
        query: { ...route.query, scopedSearch: undefined },
      },
      { replace: true },
    );
  } else {
    resetSearch();
  }
}

function retrySearch() {
  loadActivities(page.value);
}

function handleFilterUpdate(key) {
  const index = filtersSelected.value.indexOf(key);

  if (index === -1) {
    filtersSelected.value.push(key);
    //Check if the key contains "lang" and if yes assign the languages selected to the selected languages array
    if (key.startsWith('lang')) {
      selectedLanguages.value.push(key.split('-')[1]);
    }
  } else {
    filtersSelected.value.splice(index, 1);
    //Remove the language from the selected languages array
    const languageToRemove = selectedLanguages.value.find((language) => language.startsWith(key.split('-')[1]));
    if (languageToRemove) {
      selectedLanguages.value = selectedLanguages.value.filter((language) => language !== languageToRemove);
    }
  }
}

async function openItineraryModal() {
  try {
    mixpanel.track('Open Itinerary Form', {
      destinationName: props.destinationName,
    });
    proxy.fbq('trackCustom', 'Open Itinerary Form');
  } catch (error) {
    console.error('Error tracking event:', error);
  }

  await navigateTo('https://forms.fillout.com/t/meVZjgxq1Rus', { external: true, target: '_blank' });
}

async function filterSelected(key) {
  handleFilterUpdate(key);
  if (filtersAutoApply.value) {
    resetSearch();
  }
}

async function priceSelected(minMax) {
  priceMinMax.value = minMax;
  //console.log(priceMinMax);
  //console.log("priceSelected priceMinMax.value", priceMinMax.value);
  if (filtersAutoApply.value) {
    resetSearch();
  }
}

async function dateSelected(dateSelected) {
  if (dateSelected) {
    date.value = `${dateSelected.getFullYear()}-${dateSelected.getMonth() + 1}-${dateSelected.getDate()}`;
  } else {
    date.value = null;
  }
  resetSearch();
  //If the datepicker is open, close it
  if (showDatePicker.value) {
    showDatePicker.value = false;
  }
}

function sortActivities(value) {
  sortby.value = value;
  resetSearch();
  // Close the dropdown
  const dropdown = document.getElementById('dropdown-sortby');
  if (dropdown) {
    dropdown.classList.add('hidden');
  }
}

function resetSearch() {
  if (abortController.value) {
    abortController.value.abort();
  }
  abortController.value = new AbortController();

  activitiesData.value = [];
  nextBatchData = null;
  excluded.value = [];
  pc_groups.value = {};
  loading.value = true;
  errorFetchingResults.value = false;
  restartLoader.value++;
  page.value = 0;
  loadActivities(0);
}

function getOptionClass(value) {
  return sortby.value === value
    ? 'hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white bg-gray-200 dark:bg-gray-800'
    : 'hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white';
}

function scrollToResults(force = false) {
  //Scroll the page with animation to the start of this component
  //Force is used when the user is performing a search from the searchbar and we want to scroll no matter what
  const component = document.getElementById('activities-search-and-filters');
  if (component) {
    const rect = component.getBoundingClientRect();
    const scrollThreshold = 200;
    const targetPosition = window.pageYOffset + rect.top - 100;
    const currentPosition = window.pageYOffset;
    const isNearTarget = Math.abs(currentPosition - targetPosition) < scrollThreshold;

    if ((rect.top < -scrollThreshold || force) && !isNearTarget) {
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth',
      });
    }
  }
}

async function loadActivities(currentPage) {
  if (isTrackingLoading.value) {
    await new Promise((resolve) => {
      const checkTracking = setInterval(() => {
        if (!isTrackingLoading.value) {
          clearInterval(checkTracking);
          resolve();
        }
      }, 100);
    });
  }
  loading.value = true;

  const params = {
    ...props.initialParams,
    text_search: scopedSearch.value || props.initialParams.text_search || '',
    page: currentPage,
    price_min_max: priceMinMax.value ? `${priceMinMax.value['min']}-${priceMinMax.value['max']}` : null,
    limit: 10,
    tags: filtersSelected.value.join(','),
    date: date.value,
    exclude: excluded.value,
    pc_groups: pc_groups.value,
    sort_by: sortby.value,
    return_facets: true,
    currency: currentCurrency.value,
    track1: trackingId.value ?? '',
    highlighted_item: highlightedItemID,
  };

  const signal = abortController.value ? abortController.value.signal : null;

  let currentResponse = null;
  if (!nextBatchData) {
    if (nextBatchAbortController.value) {
      nextBatchAbortController.value.abort();
    }
    nextBatchAbortController.value = new AbortController();
    const resp = await useFetch(`/api/search`, {
      params: params,
      signal: signal,
      server: false,
      lazy: true,
    });
    currentResponse = resp;
  } else {
    await new Promise((resolve) => setTimeout(resolve, 300));
    currentResponse = nextBatchData;
    nextBatchData = null;
  }

  if (currentResponse.data.value) {
    loading.value = false;
    errorFetchingResults.value = false;
    page.value += 1;
    metaData.value = currentResponse.data.value.meta;
    excluded.value = [...excluded.value, ...metaData.value.exclude];
    pc_groups.value = metaData.value.pc_groups;

    if (currentPage === 0) {
      activitiesData.value = currentResponse.data.value.items;
      resultsCount.value = currentResponse.data.value.meta.total_results;

      // Reset filters only if results are found and no filters are selected
      if (activitiesData.value.length > 0 && filtersData.value.length === 0) {
        filtersData.value = metaData.value.filters;

        function find_label(linked_param, filtersList){
          const label_to_find = decodeURIComponent(linked_param).toLowerCase();
          const filter = filtersList.find(
            (item) => item.old_label.toLowerCase().replace(/\s+/g, '-') === label_to_find
              || item.label.toLowerCase().replace(/\s+/g, '-') === label_to_find
          );
          const key = filter ? filter.key : undefined;
          if (key) handleFilterUpdate(key);
          return !!key;
        }

        if(props.initialParams.linked_tag) {
          const found = find_label(props.initialParams.linked_tag, filtersData.value.Features?.filters ?? []) || find_label(props.initialParams.linked_tag, filtersData.value['Type of Tours']?.filters ?? []);
          if (found) props.initialParams.linked_tag = null;
        }
        if(props.initialParams.linked_identity) {
          const found = find_label(props.initialParams.linked_identity, filtersData.value['Additional Landmarks']?.filters ?? []) || find_label(props.initialParams.linked_identity, filtersData.value.Features?.filters ?? []);
          if (found) props.initialParams.linked_identity = null;
        }
      }

      scrollToResults();
    } else {
      activitiesData.value = [...activitiesData.value, ...currentResponse.data.value.items];
    }

    emit('update:resultsCount', resultsCount.value);
  } else if (currentResponse.status == 'error') {
    errorFetchingResults.value = true;
    loading.value = false;
    emit('error', 'Error fetching results');
    return;
  }

  // Update only the activities #, not the price range
  for (const [key, filterSection] of Object.entries(filtersData.value)) {
    if (key === 'Price' || page.value !== 1) {
      continue;
    }
    for (const filter of filterSection.filters) {
      if (metaData.value) {
        const matchingFilter = metaData.value.filters[key]?.filters.find((f) => f.label === filter.label);
        filter.value = matchingFilter ? matchingFilter.value : 0;
      } else {
        filter.value = 0;
      }
    }
  }

  if (metaData.value.thereAreMore) {
    params.exclude = excluded.value;
    params.pc_groups = pc_groups.value;
    params.page = page.value;
    params.limit = 10;
    const nextBatchSignal = nextBatchAbortController.value ? nextBatchAbortController.value.signal : null;
    const nextResponse = await useFetch(`/api/search`, {
      params: params,
      signal: nextBatchSignal,
      server: false,
      lazy: true,
    });
    nextBatchData = nextResponse;
  }
}

function loadMoreActivities() {
  loadActivities(page.value);
}

// Modal functions
function openFilters() {
  isModalOpen.value = true;
}

function closeFilters() {
  isModalOpen.value = false;
}

function applyFilters() {
  resetSearch();
  closeFilters();
}

// Lifecycle hooks
onMounted(() => {
  updateWidth();
  window.addEventListener('resize', updateWidth);
  loadActivities(0);
});

onBeforeMount(() => {
  loading.value = true;
  updateWidth();
  if (process.client) {
    loadActivities(0);
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', updateWidth);
});

// Watchers
watch(
  () => props.initialParams,
  () => {
    filtersData.value = [];
    resetSearch();
  },
  { deep: true },
);

// Responsive design
const width = ref(0);

function updateWidth() {
  if (typeof window !== 'undefined') {
    width.value = window.innerWidth;
    filtersAutoApply.value = width.value > 768;
  }
}

function toggleDatePicker() {
  showDatePicker.value = !showDatePicker.value;
  //If we show the datepicker lets disable scroll
  if (showDatePicker.value) {
    addClickOutsideListener();
  } else {
    removeClickOutsideListener();
  }
}

// const selectDateMobileButtonPosition = computed(() => { DEPRECATED
//   if (mobileButtonSelectDate.value) {
//     const rect = mobileButtonSelectDate.value.getBoundingClientRect();
//     return {
//       left: rect.left + rect.width / 2,
//       top: rect.bottom,
//     };
//   }
//   return { left: '50%', top: 0 };
// });

let clickOutsideListener;

function addClickOutsideListener() {
  clickOutsideListener = (event) => {
    if (
      datepickerRef.value &&
      !datepickerRef.value.contains(event.target) &&
      !mobileButtonSelectDate.value.contains(event.target)
    ) {
      toggleDatePicker();
    }
  };
  document.addEventListener('click', clickOutsideListener, true);
}

function removeClickOutsideListener() {
  document.removeEventListener('click', clickOutsideListener, true);
}

// Expose necessary properties and methods
defineExpose({
  resultsCount,
  errorFetchingResults,
  resetSearch,
  loadMoreActivities,
});
</script>

<style scoped>
/* Add any scoped styles here */
</style>
